import React, { useState } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Tabs } from 'antd'
import { useSelector } from 'react-redux'
import {
  PageHeader,
  FilterContainer,
  RangePicker,
  GroupedResidentsMultiSelect,
  Table,
  Avatar,
  Button,
  Tag
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { Home } from '@lmc/ts-common'
import useResidentsList from '../../../../Rostering/hooks/useResidentsList'
import PageWrapper from '../componentsV2/PageWrapper'
import Warning from '../iconsV2/Warning'
import Success from '../iconsV2/Success'

type DiscrepancyRecord = {
  id: string
  timeRecorded: string
  serviceUser: {
    id: string
    name: string
    picture?: string
  }
  medication: {
    name: string
    prn?: boolean
    controlledDrug?: boolean
  }
  stockCount: number
  expectedStock: number
  reason?: string
}

const mockRecords: DiscrepancyRecord[] = [
  {
    id: '1',
    timeRecorded: '2025-09-11T08:50:00',
    serviceUser: {
      id: '1',
      name: 'Fred Jones',
      picture: ''
    },
    medication: {
      name: 'Paracetamol 500mg tablets',
      prn: true
    },
    stockCount: 52,
    expectedStock: 50
  },
  {
    id: '2',
    timeRecorded: '2025-09-11T08:50:00',
    serviceUser: {
      id: '2',
      name: 'Julie Freeman',
      picture: ''
    },
    medication: {
      name: 'Amoxapine 50m tablets',
      controlledDrug: true
    },
    stockCount: 50,
    expectedStock: 1,
    reason: 'Stock adjustment made'
  }
]

export const StockDiscrepanciesReportScreenV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const preferredPeopleNominative = home?.preferredPeopleNominative
  const { residents } = useResidentsList({})

  const [activeTab, setActiveTab] = useState('open')
  const [startDate, setStartDate] = useState(
    dayjs().subtract(30, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(dayjs().endOf('day'))
  const [selectedResidents, setSelectedResidents] = useState<string[]>([])

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day'))
      setEndDate(dates[1].endOf('day'))
    }
  }

  const filteredRecords = mockRecords.filter((record) => {
    if (activeTab === 'open') {
      return !record.reason
    }
    return !!record.reason
  })

  const tableColumns = [
    {
      title: t('TIME_RECORDED'),
      dataIndex: 'timeRecorded',
      key: 'timeRecorded',
      width: 180,
      render: (timeRecorded: string) => (
        <Text>{dayjs(timeRecorded).format('DD-MM-YY HH:mm')}</Text>
      )
    },
    {
      title: preferredPeopleNominative,
      dataIndex: 'serviceUser',
      key: 'serviceUser',
      width: 200,
      render: (serviceUser: DiscrepancyRecord['serviceUser']) => (
        <ServiceUserCell>
          <Avatar url={serviceUser.picture as unknown as string} size={30} />
          <Text>{serviceUser.name}</Text>
        </ServiceUserCell>
      )
    },
    {
      title: t('MEDICATION'),
      dataIndex: 'medication',
      key: 'medication',
      width: 250,
      render: (medication: DiscrepancyRecord['medication']) => (
        <MedicationCell>
          <Text>{medication.name}</Text>
          <BadgeContainer>
            {medication.prn && <Tag color="blue">PRN</Tag>}
            {medication.controlledDrug && <Tag color="red">CD</Tag>}
          </BadgeContainer>
        </MedicationCell>
      )
    },
    {
      title: t('STOCK_COUNT'),
      dataIndex: 'stockCount',
      key: 'stockCount',
      width: 150,
      align: 'center' as const,
      render: (stockCount: number, record: DiscrepancyRecord) => (
        <StockCountCell>
          <Text>{stockCount}</Text>
          {!record.reason ? (
            <IconWrapper>
              <Warning />
            </IconWrapper>
          ) : (
            <IconWrapper>
              <Success />
            </IconWrapper>
          )}
        </StockCountCell>
      )
    },
    {
      title: t('EXPECTED_STOCK'),
      dataIndex: 'expectedStock',
      key: 'expectedStock',
      width: 150,
      align: 'center' as const,
      render: (expectedStock: number) => <Text>{expectedStock}</Text>
    },
    {
      dataIndex: 'reason',
      key: 'reason',
      width: 236,
      render: (reason: string | undefined, record: DiscrepancyRecord) => (
        <ReasonCell>
          {reason ? (
            <Text>{reason}</Text>
          ) : (
            <Button variant="default">{t('RESOLVE_DISCREPANCY')}</Button>
          )}
        </ReasonCell>
      )
    }
  ]

  const tabItems = [
    {
      key: 'open',
      label: <Text>{t('OPEN_DISCREPANCIES')}</Text>,
      children: (
        <Table
          columns={tableColumns}
          dataSource={filteredRecords}
          rowKey="id"
          pagination={false}
        />
      )
    },
    {
      key: 'resolved',
      label: <Text>{t('RESOLVED_DISCREPANCIES')}</Text>,
      children: (
        <Table
          columns={tableColumns}
          dataSource={filteredRecords}
          rowKey="id"
          pagination={false}
        />
      )
    }
  ]

  return (
    <>
      <PageHeader title={t('STOCK_DISCREPANCIES')} sticky />
      <PageWrapper>
        <FilterContainer title="">
          <>
            <RangePicker
              defaultValue={[startDate, endDate]}
              value={[startDate, endDate]}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
            />
            <GroupedResidentsMultiSelect
              residents={residents}
              title={preferredPeopleNominative || 'Service Users'}
              values={selectedResidents}
              handleInputChange={setSelectedResidents}
              hasClear
              hasSearch
              hasSelectAll
            />
          </>
        </FilterContainer>
        <StyledTabs
          defaultActiveKey="open"
          activeKey={activeTab}
          items={tabItems}
          onChange={setActiveTab}
        />
      </PageWrapper>
    </>
  )
}

const ServiceUserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const BadgeContainer = styled.div`
  display: flex;
  gap: 4px;
`

const StockCountCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const IconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  width: 14px;
`

const ReasonCell = styled.div`
  display: flex;
  align-items: center;
`

const StyledTabs = styled(Tabs)`
  width: 100%;
  .ant-tabs-nav {
    margin: 0 0 24px 24px;
  }
`
